import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import gsap from 'gsap/gsap-core';
import ScrollTrigger from 'gsap/ScrollTrigger';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import ImagesLoaded from 'react-images-loaded';
import SEO from '../../components/SEO';
import NavExpander from '../../containers/NavExpander';
import Logo from './logo';
import styles from './inspirationGallery.css';

const CHANNEL_SLUG = 'inspiration-nation';
const COUNT = 20;

gsap.defaults({ duration: 0.6, ease: 'power3.inOut' });
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const InspirationGallery = () => {
  const [blocks, setBlocks] = useState([]);
  const [logoAnimationComplete, setLogoAnimationComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const blocksRef = [];
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);

  const animateIn = image => {
    gsap
      .timeline()
      .set(image, {
        rotateX: `${gsap.utils.random(-10, 10)}deg`,
        rotateY: `${gsap.utils.random(-10, 10)}deg`,
        rotateZ: `${gsap.utils.random(-10, 10)}deg`,
      })
      .to(image, {
        rotateX: `0deg`,
        rotateY: `0deg`,
        rotateZ: `0deg`,
        duration: 4,
      })
      .set(image, { clearProps: 'all' });
  };

  const handleDone = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    async function getChannelContent() {
      try {
        const response = await fetch(
          `https://api.are.na/v2/channels/${CHANNEL_SLUG}/contents?&per=${COUNT}`,
        );
        return response.json();
      } catch (error) {
        console.error(`📣: InspirationGallery -> getChannelContent -> error`, error);
        throw error;
      }
    }

    async function getBlocks() {
      const arenaBlocks = await getChannelContent();
      setBlocks(arenaBlocks['contents']);
    }

    getBlocks();
  }, []);

  useEffect(() => {
    if (blocksRef.length > 0) {
      blocksRef.forEach(block => {
        const image = block.querySelector('img');
        animateIn(image);
      });
    }
  }, [logoAnimationComplete]);

  useEffect(() => {
    if (ScrollTrigger.maxScroll(wrapperRef.current) > 0) {
      const tl = gsap.timeline();

      // handles the infinite part, wrapping around at either end...
      ScrollTrigger.create({
        scroller: wrapperRef.current,
        start: 0,
        end: () => ScrollTrigger.maxScroll(wrapperRef.current) - 1,
        onLeaveBack: self => self.scroll(ScrollTrigger.maxScroll(wrapperRef.current) - 2),
        onLeave: self => self.scroll(2),
      }).scroll(2);

      gsap.set(contentRef.current, { y: '-100vh' });
      gsap.to(wrapperRef.current, { scrollTo: 'max', duration: 0.1 });

      tl.to(contentRef.current, { y: '0vh', duration: 2.5 }).from(
        wrapperRef.current,
        {
          duration: 4,
          scrollTo: 'max',
        },
        '<',
      );

      gsap.to(blocksRef, {
        autoAlpha: 1,
      });
    }

    if (blocksRef.length > 0) {
      blocksRef.forEach(block => {
        const image = block.querySelector('img');
        animateIn(image);
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(st => st.kill());
    };
  }, [logoAnimationComplete]);

  return (
    <div>
      <SEO
        title="Inspiration Nation Gallery"
        path="/inspiration-gallery"
      />
      <NavExpander isInspirationGallery tabIndex="0">
        <>
          {isLoading && (
            <div className={styles.loading}>
              <span className={styles.loadingText}>Loading...</span>
            </div>
          )}
          <Logo setLogoAnimationComplete={setLogoAnimationComplete} />
          <section ref={wrapperRef} className={styles.wrapper}>
            <div ref={contentRef}>
              <ImagesLoaded done={handleDone} className={styles.content}>
                {logoAnimationComplete &&
                  blocks.map((block, index) => {
                    const aTag = /<a (.*)>/g;
                    const htmlWithNewTabLinks = block.description_html.replace(
                      aTag,
                      '<a target="_blank" $1>',
                    );
                    return (
                      <div
                        key={`image_${index}`}
                        ref={el => blocksRef.push(el)}
                        className={styles.block}>
                        <div className={styles.blockContent}>
                          <img
                            className={styles.image}
                            src={block.image.display.url}
                            alt={block.image.display.alt}
                          />
                          <div className={styles.details}>
                            <div className={styles.title}>{block.title}</div>
                            <div
                              className={styles.description}
                              dangerouslySetInnerHTML={{ __html: htmlWithNewTabLinks }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {/* repeat of the first two images at the end to make the infinite scroll seamless */}
                {logoAnimationComplete &&
                  (blocks.length % 2 === 0 ? (
                    blocks.slice(0, 2).map((block, index) => (
                      <div key={index} ref={el => blocksRef.push(el)} className={styles.block}>
                        <div className={styles.blockContent}>
                          <img
                            className={styles.image}
                            src={block.image.display.url}
                            alt={block.image.display.alt}
                          />
                          <div className={styles.details}>
                            <div className={styles.title}>{block.title}</div>
                            <div
                              className={styles.description}
                              dangerouslySetInnerHTML={{ __html: block.description_html }}
                            />
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <>
                      <div ref={el => blocksRef.push(el)} className={styles.block}>
                        <div className={styles.blockContent}>
                          <img
                            className={styles.image}
                            src={blocks[2].image.display.url}
                            alt={blocks[2].image.display.alt}
                          />
                          <div className={styles.details}>
                            <div className={styles.title}>{blocks[2].title}</div>
                            <div
                              className={styles.description}
                              dangerouslySetInnerHTML={{ __html: blocks[2].description_html }}
                            />
                          </div>
                        </div>
                      </div>
                      {blocks.slice(0, 2).map((block, index) => (
                        <div key={index} ref={el => blocksRef.push(el)} className={styles.block}>
                          <div className={styles.blockContent}>
                            <img
                              className={styles.image}
                              src={block.image.display.url}
                              alt={block.image.display.alt}
                            />
                            <div className={styles.details}>
                              <div className={styles.title}>{block.title}</div>
                              <div
                                className={styles.description}
                                dangerouslySetInnerHTML={{ __html: block.description_html }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  ))}
              </ImagesLoaded>
            </div>
          </section>
        </>
      </NavExpander>
    </div>
  );
};

export default withRouter(InspirationGallery);
